import { useEffect, useRef, useState } from 'react';

import debounce from '@snowit/utils/debounce';

type WindowDimensions = {
  width: number;
  height: number;
  outerWidth: number;
  outerHeight: number;
};

/** Pure read - safe to call during render. */
const measure = (): WindowDimensions => {
  const doc = document.documentElement || document.body;
  const { innerWidth, innerHeight, outerWidth, outerHeight } = window;
  const { clientWidth, clientHeight } = doc;

  return {
    width: clientWidth || innerWidth,
    height: clientHeight || innerHeight,
    outerWidth,
    outerHeight,
  };
};

/** The DOM write, kept out of the render phase. */
const publishCssVars = ({ width, height }: WindowDimensions) => {
  document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
  document.documentElement.style.setProperty('--vw', `${width * 0.01}px`);
};

const sameDimensions = (a: WindowDimensions, b: WindowDimensions) =>
  a.width === b.width &&
  a.height === b.height &&
  a.outerWidth === b.outerWidth &&
  a.outerHeight === b.outerHeight;

export default (time = 150) => {
  // Lazy initialiser: measuring forces a layout read, so it must not run on
  // every render. The CSS variables are published from the effect below, since
  // an initialiser runs twice under StrictMode and render must stay pure.
  const [windowDimensions, setWindowDimensions] = useState(measure);
  const currentRef = useRef(windowDimensions);

  useEffect(() => {
    publishCssVars(currentRef.current);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const onResize = debounce(() => {
      if (cancelled) return;

      const next = measure();
      // Mobile URL-bar movement fires resize with identical dimensions.
      if (sameDimensions(next, currentRef.current)) return;

      currentRef.current = next;
      publishCssVars(next);
      setWindowDimensions(next);
    }, time);

    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
    };
  }, [time]);

  return windowDimensions;
};
